import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Neues Passwort, OM Selected" },
      { name: "description", content: "Vergib ein neues Passwort für dein OM Selected Konto." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Supabase verarbeitet den Recovery-Hash automatisch und löst PASSWORD_RECOVERY aus.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setReady(true);
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError(t("auth.minChars"));
      return;
    }
    if (password !== confirm) {
      setError(t("auth.mismatch"));
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setDone(true);
      setTimeout(() => navigate({ to: "/admin" }), 1500);
    } catch (err: any) {
      setError(err.message ?? t("auth.saveError"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-md px-5 md:px-10 py-20">
      <div className="eyebrow text-muted-foreground">{t("auth.editorial")}</div>
      <h1 className="serif text-4xl md:text-5xl mt-4">{t("auth.resetTitle")}</h1>
      {done ? (
        <p className="mt-10 text-muted-foreground">{t("auth.resetDone")}</p>
      ) : !ready ? (
        <p className="mt-10 text-muted-foreground">
          {t("auth.resetChecking")}{" "}
          <Link to="/forgot-password" className="underline">{t("auth.resetCheckingLink")}</Link> {t("auth.resetCheckingSuffix")}
        </p>
      ) : (
        <form onSubmit={onSubmit} className="mt-10 space-y-6">
          <label className="block">
            <div className="eyebrow text-muted-foreground mb-2">{t("auth.newPassword")}</div>
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border-b border-foreground bg-transparent py-2 outline-none"
            />
          </label>
          <label className="block">
            <div className="eyebrow text-muted-foreground mb-2">{t("auth.confirmPassword")}</div>
            <input
              type="password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full border-b border-foreground bg-transparent py-2 outline-none"
            />
          </label>
          {error && <div className="text-sm text-destructive">{error}</div>}
          <button
            type="submit"
            disabled={busy}
            className="eyebrow border border-foreground px-6 py-3 hover:bg-foreground hover:text-background transition-colors disabled:opacity-50"
          >
            {busy ? t("auth.saving") : t("auth.savePassword")}
          </button>
        </form>
      )}
      <Link to="/login" className="eyebrow inline-block mt-12 border-b border-foreground pb-1">
        {t("common.backToLogin")}
      </Link>
    </div>
  );
}